import axios from 'axios';
import vopayApi from '@/services/api.config';
import { SupportedCurrencies } from 'vopay';
import { useEffect, useState } from 'react';

const { api } = vopayApi;

export type Balance = {
  AccountBalance: number;
  AvailableFunds: number;
  Currency: SupportedCurrencies;
  PendingFunds: number;
  Reserve: number;
  RollingReserveNSF: number;
  RollingReserveReturns: number;
  RollupAvailableBalance: number | null;
};

export default function useAccountBalances() {
  const source = axios.CancelToken.source();
  const [balances, setBalances] = useState<Balance[]>([]);
  const [fetchingBalances, setFetchingBalances] = useState(false);
  const [hasFetchedBalances, setHasFetchedBalances] = useState(false);
  const [hasBalancesError, setHasBalancesError] = useState(false);

  useEffect(() => {
    // Swallow the rejection here: the auto-fetch drives its UI off hasBalancesError, not a throw.
    // fetchBalances still rethrows so callers that await it directly (AccountSummaryPage) can catch.
    fetchBalances().catch(() => {});

    return () => source.cancel();
  }, []);

  async function fetchBalances() {
    try {
      setFetchingBalances(true);
      const response = await api.fetchAccountBalances({}, source);

      if (!response.Success) {
        throw new Error(response.ErrorMessage);
      }

      const parsedBalances = response.AccountBalances.map((balance) => ({
        AccountBalance: balance.AccountBalance,
        AvailableFunds: balance.AvailableFunds,
        Currency: balance.Currency,
        PendingFunds: balance.PendingFunds,
        Reserve: balance.Reserve,
        RollingReserveNSF: balance.RollingReserveNSF ?? 0,
        RollingReserveReturns: balance.RollingReserveReturns ?? 0,
        RollupAvailableBalance: balance.RollupAvailableBalance ?? null,
      }));

      setBalances(parsedBalances);
      setHasBalancesError(false);
    } catch (err) {
      // The unmount cleanup calls source.cancel(), which rejects the in-flight request with an
      // axios Cancel. That is not a real failure — bail before flagging an error, otherwise a
      // fast navigate-away (or a StrictMode double-mount in dev) surfaces a bogus error dialog.
      if (axios.isCancel(err)) return;
      console.error(err);
      // Set the boolean AND rethrow. TransactionsHistory only auto-fetches (the mount effect,
      // which can't catch a throw), so it reads hasBalancesError to drive its dialog. But
      // AccountSummaryPage awaits fetchBalances() directly inside its own try/catch and needs the
      // throw to reach it — otherwise a failed balances refresh resolves silently and it shows a
      // success toast over stale balances. The mount effect above swallows the rethrow with .catch().
      setHasBalancesError(true);
      throw err;
    } finally {
      setFetchingBalances(false);
      setHasFetchedBalances(true);
    }
  }

  return {
    fetchingBalances,
    hasFetchedBalances,
    hasBalancesError,
    balances,
    fetchBalances,
  };
}
